import { useNavigate, useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { LogOut } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Typography } from "@/components/ui/typography";
import ThemeToggle from "@/components/ui/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ヘッダー外枠：画面幅いっぱい・背景は card・下線。スクロールに追従。
const HEADER_BASE =
  "sticky top-0 z-[var(--z-sticky)] w-full border-b border-border bg-card/80 backdrop-blur";
const HEADER_INNER = "flex h-14 w-full items-stretch gap-2 px-3 sm:gap-4 sm:px-4";

// 対戦画面（/game/:roomId）は対戦と一人回しで共用で、一人回しは ?solo=1 付きで開く（PracticePage）。
const isSoloGame = (p: string, search: string) =>
  p.startsWith("/game") && new URLSearchParams(search).get("solo") === "1";

// ナビ項目。/ はデッキリスト（ホーム）、/lobby は対戦、/practice は一人回し。
// 対戦画面は、一人回しなら「練習」、それ以外は「対戦」をアクティブにする。
const NAV_ITEMS: {
  label: string;
  path: string;
  match: (p: string, search: string) => boolean;
}[] = [
  { label: "デッキリスト", path: "/", match: (p) => p === "/" || p.startsWith("/deck") },
  {
    label: "対戦",
    path: "/lobby",
    match: (p, s) => p.startsWith("/lobby") || (p.startsWith("/game") && !isSoloGame(p, s)),
  },
  {
    label: "練習",
    path: "/practice",
    match: (p, s) => p.startsWith("/practice") || isSoloGame(p, s),
  },
];

export default function AppHeader({
  children,
  className = "",
  showNav = true,
}: {
  children?: React.ReactNode;
  className?: string;
  showNav?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();

  const initial = user?.displayName?.trim().charAt(0).toUpperCase() || "?";

  return (
    <header className={twMerge(HEADER_BASE, className)}>
      <div className={HEADER_INNER}>
        <button
          onClick={() => navigate("/")}
          aria-label="ホームへ"
          className="flex items-center gap-2 self-center shrink-0 text-base font-bold tracking-tight transition-opacity hover:opacity-80"
        >
          <img src="/logo.svg" alt="" className="size-7" />
          <span className="text-foreground">DM Online</span>
        </button>

        {showNav && (
          <nav className="ml-1 flex items-stretch gap-0.5 shrink-0 sm:ml-4 sm:gap-1">
            {NAV_ITEMS.map((item) => {
              const active = item.match(location.pathname, location.search);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  aria-current={active ? "page" : undefined}
                  className={`relative flex items-center whitespace-nowrap px-1.5 text-[13px] transition-colors sm:px-2.5 sm:text-sm ${
                    active
                      ? "font-semibold text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                  <span
                    aria-hidden
                    className={`absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary transition-opacity ${
                      active ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </button>
              );
            })}
          </nav>
        )}

        {children}

        <div className="ml-auto flex items-center gap-2 self-center shrink-0 sm:gap-3">
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="ユーザーメニュー"
                className="flex rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Avatar>
                  {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
                  <AvatarFallback className="font-semibold">{initial}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="flex flex-col gap-0.5">
                <Typography variant="body" as="span" className="truncate font-semibold">
                  {user?.displayName}
                </Typography>
                {user?.email && (
                  <Typography variant="caption" tone="muted" as="span" className="truncate font-normal">
                    {user.email}
                  </Typography>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => clearAuth()}>
                <LogOut />
                ログアウト
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
